import { ClubOnboardingSettingsDTO } from "@gymflow/types";
import { motion } from "framer-motion";
import { useQueryMemberProfileAsMember } from "libs/api/src/lib/hooks/memberAsMember/useQueryMemberProfile";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { FormImageInput } from "../../components/atoms/form/FormImageInput";
import { FormRadioButtonGroup } from "../../components/atoms/form/FormRadioButtonGroup";
import { FormSingleDatePicker } from "../../components/atoms/form/FormSingleDatePicker";
import { FormInput } from "../../components/atoms/form/text/FormInput";
import { FormPhoneNumberInput } from "../../components/atoms/form/text/FormPhoneNumberInput";
import useGymflowModels from "../../store";
import { AuthenticationContext } from "../authentication/AuthenticationContext";

export const OnboardingFormStep1PersonalDetails = ({
  onboardingSettings,
}: {
  onboardingSettings: ClubOnboardingSettingsDTO;
}) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { userId: id } = useContext(AuthenticationContext);
  const { data: member } = useQueryMemberProfileAsMember({
    api,
    memberId: id,
  });
  return (
    <motion.div
      className="flex w-full max-w-lg flex-col gap-3"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <div className="dark:text-gray-0 font-semibold text-gray-950">
        {t("pages.onboarding.personalDetails")}
      </div>
      <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {onboardingSettings.pictureSetting !== "DISABLED" && (
          <FormImageInput
            name="picture"
            label={t("pages.onboarding.picture")}
            modalTitle={t("pages.onboarding.picture")}
            placeholder={`${member?.firstName[0]}${member?.lastName[0]}`}
            containerClassName="w-full items-center justify-center"
            isRequired={onboardingSettings.pictureSetting === "REQUIRED"}
          />
        )}

        {onboardingSettings.mobileNumberSetting !== "DISABLED" && (
          <FormPhoneNumberInput
            name="mobileNumber"
            label={t("pages.onboarding.mobilePhone")}
            isRequired={onboardingSettings.mobileNumberSetting === "REQUIRED"}
            placeholder={t("pages.onboarding.mobilePhone")}
          />
        )}
        {onboardingSettings.dateBirthSetting !== "DISABLED" && (
          <FormSingleDatePicker
            name="dateBirth"
            label={t("pages.onboarding.dateOfBirth")}
            isRequired={onboardingSettings.dateBirthSetting === "REQUIRED"}
            useDateOnly
            useIsoString
            captionLayout="dropdown"
          />
        )}
        {onboardingSettings.genderSetting !== "DISABLED" && (
          <FormRadioButtonGroup
            name="gender"
            label={t("pages.onboarding.gender")}
            options={[
              { value: "MALE", label: t("pages.onboarding.MALE") },
              { value: "FEMALE", label: t("pages.onboarding.FEMALE") },
              {
                value: "PREFER_NOT_TO_SAY",
                label: t("pages.onboarding.PREFER_NOT_TO_SAY"),
              },
            ]}
            isRequired={onboardingSettings.genderSetting === "REQUIRED"}
          />
        )}
        {onboardingSettings.personalNumberSetting !== "DISABLED" && (
          <FormInput
            name="personalNumber"
            label={t("pages.onboarding.personalNumber")}
            isRequired={onboardingSettings.personalNumberSetting === "REQUIRED"}
            placeholder={t("pages.onboarding.personalNumber")}
          />
        )}
      </div>
    </motion.div>
  );
};
